import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Download, Loader2, ArrowLeft, ArrowRight, Sparkles, Upload, Eye, Info, FileText, Check } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/hooks/use-toast';
import { useRFI } from '@/context/RFIContext';
import { createRfi, getProjectWithRfi } from '@/lib/api/projects';
import {
  documentBatchUploadFeedback,
  getDocumentDownloadUrl,
  listProjectDocuments,
  uploadProjectDocuments,
} from '@/lib/api/documents';
import { BackendError } from '@/lib/backend';
import type { BusinessSize, DocumentResponse, RFICreate, ProjectListItem, ProjectWithRFIResponse } from '@/types/backend';
import { IndustryPicker } from '@/components/rfi/IndustryPicker';
import { INDUSTRIES } from '@/data/industries';
import { AppHeader } from '@/components/AppHeader';
import { useProjectRole, NO_PERMISSION_TOOLTIP } from '@/hooks/useProjectRole';
import { cn } from '@/lib/utils';
import wordIcon from '@/assets/word-icon.png';

const DEMO_MODE = import.meta.env?.VITE_DEMO_MODE === 'true';

type RFIStep = 'form';

const FOCUS_ITEMS = [
  { id: 'cash_revenue_recon', label: 'Cash to revenue reconciliation' },
  { id: 'daily_cash', label: 'Daily cash balances' },
  { id: 'fx_analysis_hedging', label: 'Foreign currency analysis and hedging' },
  { id: 'constant_currency', label: 'Constant currency analysis' },
  { id: 'customer_contracts', label: 'Understand contractual relationship with key customers' },
  { id: 'supplier_contracts', label: 'Understand contractual relationship with key suppliers' },
  { id: 'mi_reporting_robustness', label: 'Review robustness of management information and financial reporting systems' },
  { id: 'review_audit_files', label: 'Review the audit files' },
];

const FISCAL_YEARS = ['FY19', 'FY20', 'FY21', 'FY22', 'FY23', 'FY24', 'FY25', 'FY26'];
const FORECAST_YEAR_COUNTS = ['1', '2', '3', '5'];
const BS_EXTENDED_FILTERS = [
  {
    id: "nwc",
    label: "Net working capital",
    children: [
      { id: "nwc_inventory", label: "Inventory" },
      { id: "nwc_receivables", label: "Trade receivables" },
      { id: "nwc_payables", label: "Trade payables" },
      { id: "nwc_accrued_rev", label: "Accrued revenue / WIP" },
      { id: "nwc_deferred_rev", label: "Deferred revenue" },
      { id: "nwc_prepayments", label: "Prepayments & deposits" },
      { id: "nwc_other", label: "Other accruals & provisions" },
      { id: "nwc_employees", label: "Employees" },
    ],
  },
  { id: "net_debt", label: "Net Debt" },
  { id: "obs", label: "Off balance sheet assets and liabilities" },
  { id: "ppe", label: "PPE" },
  { id: "intangibles", label: "Intangibles / IP" },
];

function parseFyToYear(fy: string): number | null {
  const m = /^FY(\d{2})$/.exec(fy);
  if (!m) return null;
  const yy = Number(m[1]);
  if (!Number.isFinite(yy)) return null;
  return 2000 + yy;
}

function yearToFy(year: number | null | undefined): string {
  if (!year || !Number.isFinite(year)) return "";
  const yy = year % 100;
  return `FY${String(yy).padStart(2, "0")}`;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function fyCount(from: string, to: string): number | null {
  const a = parseInt(from.replace("FY", ""));
  const b = parseInt(to.replace("FY", ""));
  if (isNaN(a) || isNaN(b) || b < a) return null;
  return b - a + 1;
}

/** Statement card with toggle header */
function StatementCard({
  title,
  enabled,
  onToggle,
  children,
  disabled,
  headerAction,
}: {
  title: string;
  enabled: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  headerAction?: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "rounded-xl border-[1.5px] overflow-hidden transition-colors",
        enabled ? "border-foreground" : "border-border"
      )}
    >
      <div
        className={cn(
          "flex items-center w-full px-4 py-3 transition-all",
          enabled ? "bg-foreground" : "bg-background",
          disabled && "opacity-50"
        )}
      >
        <button
          type="button"
          disabled={disabled}
          onClick={onToggle}
          className={cn(
            "flex items-center gap-2.5 cursor-pointer flex-1 min-w-0",
            disabled && "cursor-not-allowed"
          )}
        >
          <div
            className={cn(
              "w-5 h-5 rounded-[5px] border-2 flex items-center justify-center transition-all shrink-0",
              enabled
                ? "border-background/60 bg-background/20"
                : "border-muted-foreground/40 bg-transparent"
            )}
          >
            {enabled && <Check className="w-3 h-3 text-background" />}
          </div>
          <span
            className={cn(
              "text-[13px] font-semibold",
              enabled ? "text-background" : "text-muted-foreground"
            )}
          >
            {title}
          </span>
        </button>
        {enabled && headerAction && (
          <div className="shrink-0 ml-2">{headerAction}</div>
        )}
      </div>
      {enabled && (
        <div className="px-4 py-3.5 bg-muted/20 border-t border-border/40">
          {children}
        </div>
      )}
    </div>
  );
}

const RFIGenerator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const projectIdFromQuery = searchParams.get("projectId");
  const { setTransactionBackground } = useRFI();
  const [currentStep, setCurrentStep] = useState<RFIStep>('form');

  useEffect(() => {
    if (location.hash !== '#transaction-background') return;
    if (currentStep !== 'form') setCurrentStep('form');
    const t = window.setTimeout(() => {
      document
        .getElementById('transaction-background')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
    return () => window.clearTimeout(t);
  }, [location.hash, currentStep]);

  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingProject, setIsLoadingProject] = useState(false);
  const [hasExistingRfi, setHasExistingRfi] = useState(false);
  const [loadedProject, setLoadedProject] = useState<ProjectWithRFIResponse | null>(null);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState<DocumentResponse[]>([]);
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);
  const [otherDatesOpen, setOtherDatesOpen] = useState(false);


  // Industry state
  const [industryId, setIndustryId] = useState<string | null>(null);
  const [industrySubtypes, setIndustrySubtypes] = useState<string[]>([]);

  // Period selection state
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{ total: number; done: number } | null>(null);

  // P&L period state
  const [pnlIncluded, setPnlIncluded] = useState(false);
  const [pnlHistoricalFrom, setPnlHistoricalFrom] = useState('');
  const [pnlHistoricalTo, setPnlHistoricalTo] = useState('');
  const [pnlYtd, setPnlYtd] = useState('');
  const [pnlForecast, setPnlForecast] = useState(false);
  const [pnlForecastYrs, setPnlForecastYrs] = useState('2');

  // BS period state
  const [bsIncluded, setBsIncluded] = useState(false);
  const [bsSameAsPnl, setBsSameAsPnl] = useState(true);
  const [bsHistoricalFrom, setBsHistoricalFrom] = useState('');
  const [bsHistoricalTo, setBsHistoricalTo] = useState('');
  const [bsYtd, setBsYtd] = useState('');
  const [bsForecast, setBsForecast] = useState(false);
  const [bsForecastYrs, setBsForecastYrs] = useState('2');

  // CF period state
  const [cfIncluded, setCfIncluded] = useState(false);
  const [cfSameAsPnl, setCfSameAsPnl] = useState(true);
  const [cfHistoricalFrom, setCfHistoricalFrom] = useState('');
  const [cfHistoricalTo, setCfHistoricalTo] = useState('');
  const [cfYtd, setCfYtd] = useState('');
  const [cfForecast, setCfForecast] = useState(false);
  const [cfForecastYrs, setCfForecastYrs] = useState('2');

  // Focus items toggle state
  const [focusToggles, setFocusToggles] = useState<string[]>([]);
  const [customFocusEnabled, setCustomFocusEnabled] = useState(false);
  const [customFocusText, setCustomFocusText] = useState('');

  // BS extended filters
  const [bsExtended, setBsExtended] = useState(false);
  const [bsExtItems, setBsExtItems] = useState<string[]>([]);
  const [bsNwcItems, setBsNwcItems] = useState<string[]>([]);
  const [fddScopeOpen, setFddScopeOpen] = useState(false);

  const toggleBsExtItem = (id: string) => {
    setBsExtItems(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    // If toggling nwc on, select all children; off, clear them
    if (id === 'nwc') {
      setBsNwcItems(prev => {
        const nwcFilter = BS_EXTENDED_FILTERS.find(f => f.id === 'nwc');
        if (!prev.length && nwcFilter?.children) return nwcFilter.children.map(c => c.id);
        return [];
      });
    }
  };

  const toggleBsNwcItem = (id: string) => {
    setBsNwcItems(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const [formData, setFormData] = useState({
    projectName: '',
    entityName: '',
    industry: '',
    jurisdictions: [] as string[],
    legalEntities: '' as BusinessSize | '',
    focusAreas: '',
  });

  const { canEdit } = useProjectRole(DEMO_MODE ? undefined : projectIdFromQuery);
  const isReadOnly = !DEMO_MODE && !canEdit;

  // Resolve industry label from id
  const industryLabel = useMemo(() => {
    if (!industryId) return '';
    for (const g of INDUSTRIES) {
      const found = g.items.find(i => i.id === industryId);
      if (found) return found.label;
    }
    return '';
  }, [industryId]);


  useEffect(() => {
    // Demo settings are local to this visit and always start empty.
    if (DEMO_MODE || !projectIdFromQuery) return;
    setSelectedFiles([]);
    setUploadedDocs([]);
    setHasExistingRfi(false);

    let cancelled = false;
    (async () => {
      setIsLoadingProject(true);
      try {
        const project = await getProjectWithRfi(projectIdFromQuery);
        if (cancelled) return;

        const rfi = project.rfi;
        setFormData((prev) => ({
          ...prev,
          projectName: project.name ?? "",
          entityName: project.entity_name ?? "",
          jurisdictions: project.jurisdictions ?? [],
          industry: "",
          legalEntities: "",
          focusAreas: "",
        }));
        setLoadedProject(project);

        setPnlIncluded(false);
        setPnlHistoricalFrom("");
        setPnlHistoricalTo("");
        setPnlForecast(false);
        setPnlForecastYrs("2");
        setPnlYtd("");
        setBsIncluded(false);
        setBsSameAsPnl(true);
        setBsHistoricalFrom("");
        setBsHistoricalTo("");
        setBsForecast(false);
        setBsForecastYrs("2");
        setBsYtd("");
        setBsExtended(false);
        setBsExtItems([]);
        setBsNwcItems([]);
        setCfIncluded(false);
        setCfSameAsPnl(true);
        setCfHistoricalFrom("");
        setCfHistoricalTo("");
        setCfForecast(false);
        setCfForecastYrs("2");
        setCfYtd("");

        setHasExistingRfi(!DEMO_MODE && !!rfi);

        if (rfi) {
          setFormData((prev) => ({
            ...prev,
            industry: rfi.industry ?? "",
            legalEntities: rfi.business_size ?? "",
            focusAreas: Array.isArray(rfi.key_focus_areas) ? rfi.key_focus_areas.join(", ") : "",
          }));

          // Restore industry picker selection + subtypes
          if (rfi.industry) {
            for (const g of INDUSTRIES) {
              const found = g.items.find(i => i.label === rfi.industry);
              if (found) { setIndustryId(found.id); break; }
            }
          }
          if (rfi.industry_subtypes?.length) {
            setIndustrySubtypes(rfi.industry_subtypes);
          }

          // Restore focus area toggles + custom
          if (Array.isArray(rfi.key_focus_areas)) {
            const restoredToggles: string[] = [];
            let customText = '';
            for (const label of rfi.key_focus_areas) {
              const item = FOCUS_ITEMS.find(f => f.label === label);
              if (item) {
                restoredToggles.push(item.id);
              } else {
                customText = label;
              }
            }
            setFocusToggles(restoredToggles);
            if (customText) {
              setCustomFocusEnabled(true);
              setCustomFocusText(customText);
            }
          }

          setPnlIncluded(!!rfi.pnl_included);
          setPnlHistoricalFrom(yearToFy(rfi.pnl_historical_from_fy));
          setPnlHistoricalTo(yearToFy(rfi.pnl_historical_to_fy));
          setPnlForecast(!!rfi.pnl_include_forecast);
          if (rfi.pnl_include_forecast && rfi.pnl_forecast_from_fy && rfi.pnl_forecast_to_fy) {
            setPnlForecastYrs(String(rfi.pnl_forecast_to_fy - rfi.pnl_forecast_from_fy));
          }

          // Restore YTD
          setPnlYtd(rfi.pnl_include_ytd ? 'YTD' : '');

          setBsIncluded(!!rfi.bs_included);
          setBsSameAsPnl(!rfi.bs_historical_different);
          setBsHistoricalFrom(yearToFy(rfi.bs_historical_from_fy));
          setBsHistoricalTo(yearToFy(rfi.bs_historical_to_fy));
          setBsYtd(rfi.bs_include_ytd ? 'YTD' : '');
          setBsForecast(!!rfi.bs_include_forecast);
          if (rfi.bs_include_forecast && rfi.bs_forecast_from_fy && rfi.bs_forecast_to_fy) {
            setBsForecastYrs(String(rfi.bs_forecast_to_fy - rfi.bs_forecast_from_fy));
          }

          if (rfi.bs_extended_filters?.length) {
            setBsExtended(true);
            setBsExtItems(rfi.bs_extended_filters);
          }
          if (rfi.bs_nwc_items?.length) {
            setBsNwcItems(rfi.bs_nwc_items);
          }

          setCfIncluded(!!rfi.cf_included);
          setCfSameAsPnl(!rfi.cf_historical_different);
          setCfHistoricalFrom(yearToFy(rfi.cf_historical_from_fy));
          setCfHistoricalTo(yearToFy(rfi.cf_historical_to_fy));
          setCfYtd(rfi.cf_include_ytd ? 'YTD' : '');
          setCfForecast(!!rfi.cf_include_forecast);
          if (rfi.cf_include_forecast && rfi.cf_forecast_from_fy && rfi.cf_forecast_to_fy) {
            setCfForecastYrs(String(rfi.cf_forecast_to_fy - rfi.cf_forecast_from_fy));
          }
        }

        try {
          const docs = await listProjectDocuments(projectIdFromQuery);
          if (!cancelled) setUploadedDocs(docs);
        } catch {
          // Silently ignore
        }
      } catch (e) {
        if (cancelled) return;
        const message =
          e instanceof BackendError
            ? `${e.message} (HTTP ${e.status})`
            : e instanceof Error
              ? e.message
              : "Unknown error";
        toast({ title: "Failed to load project", description: message, variant: "destructive" });
      } finally {
        if (!cancelled) setIsLoadingProject(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectIdFromQuery]);

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const hasSelectedFiles = useMemo(() => selectedFiles.length > 0, [selectedFiles.length]);

  const handleGenerateRFI = async () => {
    if (!projectIdFromQuery) {
      toast({ title: 'No project selected', description: 'Please create a project first.', variant: 'destructive' });
      return;
    }
    if (!formData.legalEntities) {
      toast({ title: 'Missing business size', variant: 'destructive' });
      return;
    }
    if (!pnlIncluded) {
      toast({ title: 'Missing required financial periods', description: 'Please include Profit & Loss and select the historical period.', variant: 'destructive' });
      return;
    }

    const focusAreas = focusToggles
      .map(id => FOCUS_ITEMS.find(f => f.id === id)?.label)
      .filter(Boolean) as string[];
    if (customFocusEnabled && customFocusText.trim()) {
      focusAreas.push(customFocusText.trim());
    }

    // Use industryLabel if available, fallback to formData.industry
    const industryValue = industryLabel || formData.industry.trim() || null;

    const rfiData: RFICreate = {
      key_focus_areas: focusAreas,
      industry: industryValue,
      industry_subtypes: industrySubtypes,
      business_size: formData.legalEntities as BusinessSize,

      pnl_included: pnlIncluded,
      pnl_historical_from_fy: parseFyToYear(pnlHistoricalFrom),
      pnl_historical_to_fy: parseFyToYear(pnlHistoricalTo),
      pnl_include_ytd: pnlIncluded && !!pnlYtd,
      pnl_include_forecast: pnlForecast,
      pnl_forecast_from_fy: pnlForecast ? parseFyToYear(pnlHistoricalTo) : null,
      pnl_forecast_to_fy: pnlForecast ? (() => {
        const toYear = parseFyToYear(pnlHistoricalTo);
        return toYear ? toYear + parseInt(pnlForecastYrs) : null;
      })() : null,

      bs_included: bsIncluded,
      bs_historical_different: bsIncluded && !bsSameAsPnl,
      bs_historical_from_fy: bsIncluded && !bsSameAsPnl ? parseFyToYear(bsHistoricalFrom) : null,
      bs_historical_to_fy: bsIncluded && !bsSameAsPnl ? parseFyToYear(bsHistoricalTo) : null,
      bs_include_ytd: bsIncluded && !!bsYtd,
      bs_include_forecast: bsIncluded ? bsForecast : false,
      bs_forecast_from_fy: bsIncluded && bsForecast ? parseFyToYear(bsHistoricalTo || pnlHistoricalTo) : null,
      bs_forecast_to_fy: bsIncluded && bsForecast ? (() => {
        const toYear = parseFyToYear(bsHistoricalTo || pnlHistoricalTo);
        return toYear ? toYear + parseInt(bsForecastYrs) : null;
      })() : null,

      bs_extended_filters: bsIncluded ? bsExtItems : [],
      bs_nwc_items: bsIncluded && bsExtItems.includes('nwc') ? bsNwcItems : [],

      cf_included: cfIncluded,
      cf_historical_different: cfIncluded && !cfSameAsPnl,
      cf_historical_from_fy: cfIncluded && !cfSameAsPnl ? parseFyToYear(cfHistoricalFrom) : null,
      cf_historical_to_fy: cfIncluded && !cfSameAsPnl ? parseFyToYear(cfHistoricalTo) : null,
      cf_include_ytd: cfIncluded && !!cfYtd,
      cf_include_forecast: cfIncluded ? cfForecast : false,
      cf_forecast_from_fy: cfIncluded && cfForecast ? parseFyToYear(cfHistoricalTo || pnlHistoricalTo) : null,
      cf_forecast_to_fy: cfIncluded && cfForecast ? (() => {
        const toYear = parseFyToYear(cfHistoricalTo || pnlHistoricalTo);
        return toYear ? toYear + parseInt(cfForecastYrs) : null;
      })() : null,
    };

    setIsGenerating(true);
    setUploadProgress(null);

    const saveTransactionBackground = () => {
      const dealTypeUI = loadedProject?.deal_type === 'sell_side' ? 'sell-side' : 'buy-side';
      setTransactionBackground({
        projectName: formData.projectName,
        entityName: formData.entityName,
        industry: industryValue || '',
        jurisdictions: formData.jurisdictions,
        legalEntities: formData.legalEntities,
        dealType: dealTypeUI,
        focusAreas: focusToggles.map(id => FOCUS_ITEMS.find(f => f.id === id)?.label).filter(Boolean).join(', '),
        pnlIncluded,
        pnlHistoricalFrom,
        pnlHistoricalTo,
        ytdMonth: pnlYtd ? 'YTD' : '',
        includeForecast: pnlForecast,
        pnlForecastFrom: '',
        pnlForecastTo: '',
        bsIncluded,
        bsHistoricalDifferent: !bsSameAsPnl,
        bsHistoricalFrom,
        bsHistoricalTo,
        bsIncludeForecast: bsForecast,
        bsForecastFrom: '',
        bsForecastTo: '',
        documentsCount: selectedFiles.length,
      });
    };

    try {
      const project: ProjectListItem = {
        id: projectIdFromQuery,
        name: formData.projectName,
        entity_name: formData.entityName,
        jurisdictions: formData.jurisdictions,
        deal_type: loadedProject?.deal_type ?? 'buy_side',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      await createRfi(project.id, rfiData);

      if (selectedFiles.length > 0) {
        setUploadProgress({ total: selectedFiles.length, done: 0 });
        const result = await uploadProjectDocuments(project.id, selectedFiles);
        setUploadProgress({ total: selectedFiles.length, done: selectedFiles.length });
        if (result.skipped.length > 0 || result.failed.length > 0) {
          toast({
            title: result.documents.length > 0 ? 'Files processed with issues' : 'Files could not be uploaded',
            description: documentBatchUploadFeedback(result),
            variant: result.documents.length === 0 ? 'destructive' : undefined,
          });
        }
      }

      saveTransactionBackground();

      toast({
        title: "Spot a gap?",
        description: "Add any missing items below. They'll be included in the download and saved to the request library for next time.",
        duration: 12000,
      });

      navigate(`/rfi-tracker?projectId=${encodeURIComponent(project.id)}`);
    } catch (e) {
      const message =
        e instanceof BackendError
          ? `${e.message} (HTTP ${e.status})`
          : e instanceof Error
            ? e.message
            : "Unknown error";
      toast({ title: "Failed", description: message, variant: "destructive" });
    } finally {
      setIsGenerating(false);
      setUploadProgress(null);
    }
  };

  const handleDownloadDocument = async (doc: DocumentResponse) => {
    if (!projectIdFromQuery) return;
    setDownloadingDocId(doc.id);
    try {
      const { url } = await getDocumentDownloadUrl(projectIdFromQuery, doc.id);
      window.open(url, "_blank");
    } catch (e) {
      const message =
        e instanceof BackendError
          ? `${e.message} (HTTP ${e.status})`
          : e instanceof Error
            ? e.message
            : "Unknown error";
      toast({ title: "Failed to download", description: message, variant: "destructive" });
    } finally {
      setDownloadingDocId(null);
    }
  };

  useEffect(() => {
    if (!DEMO_MODE || !isGenerating) return;
    const timer = window.setTimeout(() => {
      navigate('/rfi-tracker' + (projectIdFromQuery ? `?projectId=${encodeURIComponent(projectIdFromQuery)}` : ''));
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [isGenerating, navigate, projectIdFromQuery]);

  const handleGenerateClick = () => {
    if (DEMO_MODE) {
      setIsGenerating(true);
      return;
    }
    if (hasExistingRfi) {
      setShowRegenerateConfirm(true);
    } else {
      handleGenerateRFI();
    }
  };

  const headerTitle = useMemo(() => {
    return loadedProject?.name || formData.projectName || undefined;
  }, [loadedProject?.name, formData.projectName]);

  const pnlSummary = () => {
    const p: string[] = [];
    if (pnlHistoricalFrom && pnlHistoricalTo) p.push(`${pnlHistoricalFrom}–${pnlHistoricalTo}`);
    if (pnlYtd) p.push('YTD');
    return p.join(" + ");
  };

  const anyForecast = pnlForecast || bsForecast;


  return (
    <div className="min-h-screen bg-background">
      <AppHeader
        showBack={true}
        backTo={projectIdFromQuery
          ? DEMO_MODE ? `/project/${encodeURIComponent(projectIdFromQuery)}` : `/databook-builder?projectId=${encodeURIComponent(projectIdFromQuery)}`
          : '/'}
        title={headerTitle}
      />
      <main className="container mx-auto px-8 py-10">
        <div className="max-w-3xl mx-auto">
          {currentStep === 'form' && (
            <div className="mb-10">
              <h1 id="transaction-background" className="text-2xl font-semibold text-foreground tracking-tight">
                {hasExistingRfi ? 'Edit RFI Parameters' : 'Information Request List'}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Configure parameters to generate a tailored FDD request list.
              </p>
            </div>
          )}

          {currentStep === 'form' && (
            <div className="space-y-14 animate-fade-in">
              <fieldset
                disabled={isReadOnly || isLoadingProject || isGenerating}
                className="contents"
              >

              {/* Business size */}
              <div className="space-y-2.5">
                <Label className="text-[15px] font-bold text-foreground tracking-tight">
                  Business size
                </Label>
                <div className="grid grid-cols-4 gap-2">
                  {([
                    { value: 'small', label: 'Small', desc: '< $50m' },
                    { value: 'mid', label: 'Mid-market', desc: '$50m – $150m' },
                    { value: 'upper_mid', label: 'Upper mid', desc: '$150m – $500m' },
                    { value: 'large', label: 'Large', desc: '> $500m' },
                  ] as const).map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleInputChange('legalEntities', option.value)}
                      disabled={isReadOnly || isLoadingProject || isGenerating}
                      className={cn(
                        "flex flex-col items-center gap-0.5 py-3 px-2 rounded-lg border-[1.5px] cursor-pointer transition-all text-center",
                        formData.legalEntities === option.value
                          ? "border-foreground bg-foreground text-background"
                          : "border-border bg-background text-muted-foreground hover:border-foreground/30"
                      )}
                    >
                      <span className="text-[13px] font-semibold">{option.label}</span>
                      <span className="text-[11px] opacity-65">{option.desc}</span>
                    </button>
                  ))}
                </div>
              </div>


              {/* Industry */}
              <div className="space-y-2.5 mt-10">
                <Label className="text-[15px] font-bold text-foreground tracking-tight">
                  Industry
                </Label>
                <IndustryPicker
                  selected={industryId}
                  onSelect={setIndustryId}
                  selectedSubtypes={industrySubtypes}
                  onSubtypesChange={setIndustrySubtypes}
                  disabled={isReadOnly || isLoadingProject || isGenerating}
                />
              </div>

              {/* Scope of analysis - P&L and BS as StatementCards */}
              <div className="space-y-2.5 mt-10">
                <div className="flex items-center gap-2.5">
                  <Label className="text-[15px] font-bold text-foreground tracking-tight">
                    Scope of analysis
                  </Label>
                  <button
                    type="button"
                    disabled={isReadOnly || isLoadingProject || isGenerating}
                    onClick={() => setFddScopeOpen(!fddScopeOpen)}
                    className={cn(
                      "px-2.5 py-1 rounded-md border text-[11px] font-semibold cursor-pointer transition-all",
                      fddScopeOpen
                        ? "border-foreground bg-foreground text-background"
                        : "border-border bg-background text-muted-foreground hover:border-foreground/40 hover:text-foreground",
                      "disabled:opacity-50 disabled:cursor-not-allowed"
                    )}
                  >
                    Full FDD scope
                  </button>
                  {fddScopeOpen && (
                    <div className="flex gap-1">
                      {[
                        { label: '1y', years: 1 },
                        { label: '2y', years: 2 },
                        { label: '3y', years: 3 },
                        { label: '4y', years: 4 },
                        { label: '5y', years: 5 },
                      ].map(preset => (
                        <button
                          key={preset.label}
                          type="button"
                          disabled={isReadOnly || isLoadingProject || isGenerating}
                          onClick={() => {
                            const toFy = 'FY25';
                            const fromIdx = FISCAL_YEARS.indexOf(toFy) - preset.years + 1;
                            const fromFy = FISCAL_YEARS[Math.max(0, fromIdx)];
                            setPnlIncluded(true);
                            setPnlHistoricalFrom(fromFy);
                            setPnlHistoricalTo(toFy);
                            setPnlYtd('YTD');
                            setPnlForecast(true);
                            setPnlForecastYrs('1');
                            setBsIncluded(true);
                            setBsSameAsPnl(true);
                            setBsForecast(false);
                            setCfIncluded(true);
                            setCfSameAsPnl(true);
                            setCfForecast(false);
                            setFocusToggles(FOCUS_ITEMS.map(f => f.id));
                            setBsExtItems(BS_EXTENDED_FILTERS.map(f => f.id));
                            const nwcFilter = BS_EXTENDED_FILTERS.find(f => f.id === 'nwc');
                            if (nwcFilter?.children) setBsNwcItems(nwcFilter.children.map(c => c.id));
                          }}
                          className={cn(
                            "px-2 py-0.5 rounded-md border text-[11px] font-semibold cursor-pointer transition-all",
                            "border-border bg-background text-muted-foreground hover:border-foreground/40 hover:text-foreground",
                            "disabled:opacity-50 disabled:cursor-not-allowed"
                          )}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2.5">
                  {/* P&L */}
                  <StatementCard
                    title="Profit & Loss"
                    enabled={pnlIncluded}
                    onToggle={() => setPnlIncluded(!pnlIncluded)}
                    disabled={isReadOnly || isLoadingProject || isGenerating}
                  >
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide mr-1">FY</span>
                        <select
                          value={pnlHistoricalFrom}
                          onChange={e => { setPnlHistoricalFrom(e.target.value); if (pnlHistoricalTo && e.target.value > pnlHistoricalTo) setPnlHistoricalTo(''); }}
                          className="px-2.5 py-1.5 rounded-lg border-[1.5px] border-border text-[13px] text-foreground bg-background cursor-pointer outline-none"
                        >
                          <option value="">From</option>
                          {FISCAL_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                        <span className="text-muted-foreground/40">→</span>
                        <select
                          value={pnlHistoricalTo}
                          onChange={e => setPnlHistoricalTo(e.target.value)}
                          className="px-2.5 py-1.5 rounded-lg border-[1.5px] border-border text-[13px] text-foreground bg-background cursor-pointer outline-none"
                        >
                          <option value="">To</option>
                          {FISCAL_YEARS.filter(y => !pnlHistoricalFrom || y >= pnlHistoricalFrom).map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                        {fyCount(pnlHistoricalFrom, pnlHistoricalTo) && (
                          <span className="text-[11px] text-muted-foreground">({fyCount(pnlHistoricalFrom, pnlHistoricalTo)}y)</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch checked={!!pnlYtd} onCheckedChange={(checked) => setPnlYtd(checked ? 'YTD' : '')} className="scale-75" />
                        <span className="text-[13px] font-medium text-foreground">Include YTD</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch checked={pnlForecast} onCheckedChange={setPnlForecast} className="scale-75" />
                        <span className="text-[13px] font-medium text-foreground">Include P&L forecast</span>
                        {pnlForecast && (
                          <div className="flex gap-1 ml-2">
                            {FORECAST_YEAR_COUNTS.map(n => (
                              <button
                                key={n}
                                type="button"
                                onClick={() => setPnlForecastYrs(n)}
                                className={cn(
                                  "px-2.5 py-1 rounded-md border-[1.5px] text-xs font-medium cursor-pointer transition-all",
                                  pnlForecastYrs === n
                                    ? "border-foreground bg-foreground text-background"
                                    : "border-border bg-background text-muted-foreground"
                                )}
                              >
                                {n}y
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </StatementCard>

                  {/* BS */}
                  <StatementCard
                    title="Balance Sheet"
                    enabled={bsIncluded}
                    onToggle={() => setBsIncluded(!bsIncluded)}
                    disabled={isReadOnly || isLoadingProject || isGenerating}
                    headerAction={
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setBsExtended(!bsExtended); }}
                        className={cn(
                          "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold cursor-pointer transition-all",
                          bsExtended
                            ? "bg-background text-foreground"
                            : "bg-primary-foreground text-primary hover:bg-primary-foreground/90"
                        )}
                      >
                        Extended filters
                        {bsExtItems.length > 0 && (
                          <span className={cn(
                            "text-[10px] font-bold px-1 py-0.5 rounded",
                            bsExtended ? "bg-foreground text-background" : "bg-background/30 text-background"
                          )}>
                            {bsExtItems.length}
                          </span>
                        )}
                      </button>
                    }
                  >
                    {!bsExtended ? (
                      /* Main BS panel */
                      <div className="flex flex-col gap-3">
                        {pnlIncluded && (
                          <div className="flex items-center gap-2">
                            <Switch checked={bsSameAsPnl} onCheckedChange={setBsSameAsPnl} className="scale-75" />
                            <span className="text-[13px] text-muted-foreground">Same as P&L</span>
                            {bsSameAsPnl && pnlSummary() && (
                              <span className="text-xs text-muted-foreground/60">({pnlSummary()})</span>
                            )}
                          </div>
                        )}
                        {(!pnlIncluded || !bsSameAsPnl) && (
                          <>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide mr-1">FY</span>
                              <select
                                value={bsHistoricalFrom}
                                onChange={e => { setBsHistoricalFrom(e.target.value); if (bsHistoricalTo && e.target.value > bsHistoricalTo) setBsHistoricalTo(''); }}
                                className="px-2.5 py-1.5 rounded-lg border-[1.5px] border-border text-[13px] text-foreground bg-background cursor-pointer outline-none"
                              >
                                <option value="">From</option>
                                {FISCAL_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                              </select>
                              <span className="text-muted-foreground/40">→</span>
                              <select
                                value={bsHistoricalTo}
                                onChange={e => setBsHistoricalTo(e.target.value)}
                                className="px-2.5 py-1.5 rounded-lg border-[1.5px] border-border text-[13px] text-foreground bg-background cursor-pointer outline-none"
                              >
                                <option value="">To</option>
                                {FISCAL_YEARS.filter(y => !bsHistoricalFrom || y >= bsHistoricalFrom).map(y => <option key={y} value={y}>{y}</option>)}
                              </select>
                              {fyCount(bsHistoricalFrom, bsHistoricalTo) && (
                                <span className="text-[11px] text-muted-foreground">({fyCount(bsHistoricalFrom, bsHistoricalTo)}y)</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Switch checked={!!bsYtd} onCheckedChange={(checked) => setBsYtd(checked ? 'YTD' : '')} className="scale-75" />
                              <span className="text-[13px] font-medium text-foreground">Include YTD</span>
                            </div>
                          </>
                        )}
                        <div className="flex items-center gap-2">
                          <Switch checked={bsForecast} onCheckedChange={setBsForecast} className="scale-75" />
                          <span className="text-[13px] font-medium text-foreground">Include BS forecast</span>
                          {bsForecast && (
                            <div className="flex gap-1 ml-2">
                              {FORECAST_YEAR_COUNTS.map(n => (
                                <button
                                  key={n}
                                  type="button"
                                  onClick={() => setBsForecastYrs(n)}
                                  className={cn(
                                    "px-2.5 py-1 rounded-md border-[1.5px] text-xs font-medium cursor-pointer transition-all",
                                    bsForecastYrs === n
                                      ? "border-foreground bg-foreground text-background"
                                      : "border-border bg-background text-muted-foreground"
                                  )}
                                >
                                  {n}y
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      /* Extended filters panel (replaces main content) */
                      <div className="flex flex-col gap-0">
                        {BS_EXTENDED_FILTERS.map((item, idx) => {
                          const on = bsExtItems.includes(item.id);
                          return (
                            <div key={item.id} className={idx < BS_EXTENDED_FILTERS.length - 1 ? "border-b border-border/40" : ""}>
                              <div className="flex items-center gap-2 py-2">
                                <Switch
                                  checked={on}
                                  onCheckedChange={() => toggleBsExtItem(item.id)}
                                  className="scale-75"
                                />
                                <span className={cn("text-[13px] font-medium", on ? "text-foreground" : "text-muted-foreground")}>
                                  {item.label}
                                </span>
                                {item.children && on && bsNwcItems.length > 0 && (
                                  <span className="text-[11px] font-semibold text-background bg-muted-foreground/70 px-1.5 py-0.5 rounded-md">
                                    {bsNwcItems.length}
                                  </span>
                                )}
                              </div>
                              {item.children && on && (
                                <div className="pl-10 pb-2.5">
                                  <div className="flex flex-wrap gap-1.5">
                                    {item.children.map(child => {
                                      const childOn = bsNwcItems.includes(child.id);
                                      return (
                                        <button
                                          key={child.id}
                                          type="button"
                                          onClick={() => toggleBsNwcItem(child.id)}
                                          className={cn(
                                            "inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-medium cursor-pointer transition-all whitespace-nowrap",
                                            childOn
                                              ? "border-foreground bg-foreground text-background"
                                              : "border-border bg-background text-muted-foreground hover:border-foreground/30"
                                          )}
                                        >
                                          {childOn && <Check className="w-2.5 h-2.5" />}
                                          {child.label}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <div className="pt-3">
                          <button
                            type="button"
                            onClick={() => setBsExtended(false)}
                            className="px-3 py-1.5 rounded-lg border-[1.5px] border-foreground bg-foreground text-background text-[12px] font-semibold cursor-pointer transition-all hover:opacity-90"
                          >
                            Done
                          </button>
                        </div>
                      </div>
                    )}
                  </StatementCard>

                  {/* Cash Flow */}
                  <StatementCard
                    title="Cash Flow"
                    enabled={cfIncluded}
                    onToggle={() => setCfIncluded(!cfIncluded)}
                    disabled={isReadOnly || isLoadingProject || isGenerating}
                  >
                    <div className="flex flex-col gap-3">
                      {pnlIncluded && (
                        <div className="flex items-center gap-2">
                          <Switch checked={cfSameAsPnl} onCheckedChange={setCfSameAsPnl} className="scale-75" />
                          <span className="text-[13px] text-muted-foreground">Same as P&L</span>
                          {cfSameAsPnl && pnlSummary() && (
                            <span className="text-xs text-muted-foreground/60">({pnlSummary()})</span>
                          )}
                        </div>
                      )}
                      {(!pnlIncluded || !cfSameAsPnl) && (
                        <>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide mr-1">FY</span>
                            <select
                              value={cfHistoricalFrom}
                              onChange={e => { setCfHistoricalFrom(e.target.value); if (cfHistoricalTo && e.target.value > cfHistoricalTo) setCfHistoricalTo(''); }}
                              className="px-2.5 py-1.5 rounded-lg border-[1.5px] border-border text-[13px] text-foreground bg-background cursor-pointer outline-none"
                            >
                              <option value="">From</option>
                              {FISCAL_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                            <span className="text-muted-foreground/40">→</span>
                            <select
                              value={cfHistoricalTo}
                              onChange={e => setCfHistoricalTo(e.target.value)}
                              className="px-2.5 py-1.5 rounded-lg border-[1.5px] border-border text-[13px] text-foreground bg-background cursor-pointer outline-none"
                            >
                              <option value="">To</option>
                              {FISCAL_YEARS.filter(y => !cfHistoricalFrom || y >= cfHistoricalFrom).map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                            {fyCount(cfHistoricalFrom, cfHistoricalTo) && (
                              <span className="text-[11px] text-muted-foreground">({fyCount(cfHistoricalFrom, cfHistoricalTo)}y)</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch checked={!!cfYtd} onCheckedChange={(checked) => setCfYtd(checked ? 'YTD' : '')} className="scale-75" />
                            <span className="text-[13px] font-medium text-foreground">Include YTD</span>
                          </div>
                        </>
                      )}
                      <div className="flex items-center gap-2">
                        <Switch checked={cfForecast} onCheckedChange={setCfForecast} className="scale-75" />
                        <span className="text-[13px] font-medium text-foreground">Include CF forecast</span>
                        {cfForecast && (
                          <div className="flex gap-1 ml-2">
                            {FORECAST_YEAR_COUNTS.map(n => (
                              <button
                                key={n}
                                type="button"
                                onClick={() => setCfForecastYrs(n)}
                                className={cn(
                                  "px-2.5 py-1 rounded-md border-[1.5px] text-xs font-medium cursor-pointer transition-all",
                                  cfForecastYrs === n
                                    ? "border-foreground bg-foreground text-background"
                                    : "border-border bg-background text-muted-foreground"
                                )}
                              >
                                {n}y
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </StatementCard>
                </div>
              </div>

              {/* Focus items toggles */}
              <div className="space-y-2.5 mt-10">
                <Label className="text-[15px] font-bold text-foreground tracking-tight">
                  Additional focus areas
                </Label>
                <div className="rounded-xl border-[1.5px] border-border overflow-hidden">
                  {FOCUS_ITEMS.map((item, idx) => {
                    const on = focusToggles.includes(item.id);
                    return (
                      <div
                        key={item.id}
                        className={cn(
                          "flex items-center gap-2.5 px-4 py-2.5",
                          idx < FOCUS_ITEMS.length - 1 && "border-b border-border/40"
                        )}
                      >
                        <Switch
                          checked={on}
                          onCheckedChange={(checked) =>
                            setFocusToggles(prev =>
                              checked ? [...prev, item.id] : prev.filter(x => x !== item.id)
                            )
                          }
                          className="scale-75"
                        />
                        <span className={cn(
                          "text-[13px] font-medium",
                          on ? "text-foreground" : "text-muted-foreground"
                        )}>
                          {item.label}
                        </span>
                      </div>
                    );
                  })}
                  {/* Custom focus area */}
                  <div className="flex items-center gap-2.5 px-4 py-2.5">
                    <Switch
                      checked={customFocusEnabled}
                      onCheckedChange={(checked) => {
                        setCustomFocusEnabled(checked);
                        if (!checked) setCustomFocusText('');
                      }}
                      className="scale-75"
                    />
                    {customFocusEnabled ? (
                      <input
                        type="text"
                        autoFocus
                        value={customFocusText}
                        onChange={e => setCustomFocusText(e.target.value.slice(0, 200))}
                        placeholder="Describe your custom focus area…"
                        className="flex-1 text-[13px] font-medium text-foreground bg-transparent border-b border-border/60 outline-none py-0.5 placeholder:text-muted-foreground/50"
                      />
                    ) : (
                      <span className="text-[13px] font-medium text-muted-foreground">
                        Custom
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              {!isReadOnly && (
              <div className="pt-10 flex gap-3">
                  <Button
                    size="lg"
                    className="flex-1 gap-2 h-12 text-[15px] font-semibold"
                    onClick={handleGenerateClick}
                    disabled={isGenerating}
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {uploadProgress
                          ? `Uploading ${uploadProgress.done}/${uploadProgress.total}...`
                          : DEMO_MODE ? "Creating RFI…" : "Creating… this may take up to a minute."}
                      </>
                    ) : (
                      <>
                        {hasExistingRfi ? 'Regenerate RFI' : 'Generate RFI'}
                      </>
                    )}
                  </Button>
                </div>
              )}

              </fieldset>

              {/* Uploaded Documents - outside fieldset */}
              {isReadOnly && uploadedDocs.length > 0 && (
                <Card variant="premium">
                  <CardHeader>
                    <CardTitle>Uploaded documents</CardTitle>
                    <CardDescription>Documents associated with this transaction</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {uploadedDocs.map((doc) => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-3 rounded-md border border-border/50 bg-muted/20 hover:bg-muted/40 transition-colors"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <FileText className="w-5 h-5 text-muted-foreground shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{doc.original_filename}</p>
                              <p className="text-xs text-muted-foreground">{formatFileSize(doc.size_bytes)}</p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="shrink-0 gap-1.5"
                            onClick={() => handleDownloadDocument(doc)}
                            disabled={downloadingDocId === doc.id}
                          >
                            {downloadingDocId === doc.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                            <span className="sr-only">Download</span>
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Regenerate Confirmation Dialog */}
      <AlertDialog open={showRegenerateConfirm} onOpenChange={setShowRegenerateConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate RFI?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the existing RFI and all associated requests, then create a new RFI from scratch. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setShowRegenerateConfirm(false); handleGenerateRFI(); }}>
              Regenerate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RFIGenerator;
